import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Button, Modal } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { EVENT, TYPE } from './ButtonPayment.constants';
import { L10N } from './ButtonPayment.l10n';
import * as style from './ButtonPayment.module.css';
import { Form } from './components/Form';
import { getComponent, getUrl } from './helpers';
import { PAYMENT_METHOD, PAYMENT_PROVIDER } from '../Payment';

const { APLAZAME, CARD, PAYPAL, PCIPROXY, TPV } = PAYMENT_METHOD;
const {
  ADDON,
  ADYEN,
  CECA,
  CYBERSOURCE,
  HOTELPAY,
  GETNET,
  LINKAGEPAY,
  MERCADOPAGO,
  MEWS,
  NUVEI,
  OPENPAY,
  PAYBYRD,
  PAYCOMET,
  PAYMOB,
  PAYNOPAIN,
  REDSYS,
  REDUNIQ,
  SHIFT4,
  STRIPE,
  WORLDLINE,
} = PAYMENT_PROVIDER;

const PROVIDERS_WITH_MODAL = [ADDON, ADYEN, NUVEI, PAYCOMET];

const ButtonPayment = ({
  children,
  config: { currencyDecimals, gateway, ...config } = {},
  dataSource = {},
  hotel: { id, ...propHotel } = {},
  info = {},
  methods = {},
  promise = () => new Promise((resolve) => resolve({})),
  tracking,
  onError = () => {},
  onPress = () => {},
  onSuccess = () => {},
  ...others
}) => {
  const { locale, translate } = useLocale();
  const { value: { forwarder, payment = dataSource || {}, hotel = propHotel || {}, urlParams } = {} } = useStore();

  const [modal, setModal] = useState(false);
  const [response, setResponse] = useState();
  const cancelled = useRef(false);

  const handleClose = () => {
    setModal(false);
    onError();
  };

  const handleModalClose = async () => {
    if (cancelled.current) return;

    cancelled.current = true;
    handleClose();

    const { checkoutCancelledUrl } = response || {};
    if (!checkoutCancelledUrl) return;

    try {
      const checkoutCancelledResponse = await fetch(checkoutCancelledUrl, { method: 'POST' });
      if (checkoutCancelledResponse.status !== 200) return;

      const { redirectUrl } = await checkoutCancelledResponse.json();
      if (redirectUrl) window.location.href = redirectUrl;
    } catch {
      return;
    }
  };

  useEffect(() => {
    if (provider === LINKAGEPAY) return;

    const { error, orderReference, token, transactionId } = payment;
    if (orderReference || token || transactionId) handleSuccess();
    else if (error) onError(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment]);

  const handlePress = async () => {
    if (onPress() === false) return;

    const { method } = payment || {};
    if (method === PCIPROXY || (method === TPV && provider === OPENPAY)) {
      return document.dispatchEvent(new window.Event(EVENT.CTA_PRESS));
    }

    const requireCardValidation = [CARD, PCIPROXY].includes(method) && cardValidation;
    const withModal =
      (PROVIDERS_WITH_MODAL.includes(provider) || methodModal) &&
      (method === TPV || requireCardValidation || isPaypalModal);
    const response =
      !withModal || isModalResponse || method === APLAZAME ? await promise(promiseData).catch(onError) : true;
    if (!response) return;

    if (withModal || response.type === TYPE.SUBMIT) setResponse(response);

    if (withModal) {
      cancelled.current = false;
      try {
        Event.publish(EVENT.OPEN_MODAL);
      } catch {
        //123 compare is failed when capture this event.
      }
      setModal(true);
    } else if (method === APLAZAME || (method === TPV && provider === LINKAGEPAY)) {
      document.dispatchEvent(new CustomEvent(EVENT.SERVICE_RESPONSE, { detail: { ...response, publicKey } }));
    } else {
      const url = getUrl({ ...hotel, id, forwarder, locale, response, tracking, urlParams });
      if (url) window.location.href = url;
    }
  };

  const handleSuccess = async (value) => {
    setModal(false);

    const response = await promise({ ...value, ...promiseData }).catch(onError);

    if (response) {
      const url = getUrl({ ...hotel, id, forwarder, locale, response, tracking, urlParams });
      if (url) {
        onSuccess(url);
        window.location.href = url;
      }
    }
  };

  const { method } = payment || {};
  const { cardValidation } = info || {};
  const methodConfig = methods[method]?.config;
  const { id: provider, publicKey } = methodConfig || {};
  const methodModal = methods[method]?.modal;
  const component = getComponent({ cardValidation, method, methodConfig: methods[method], provider });
  const isPaypalModal = method === PAYPAL && methods[method]?.modal;
  const isModalResponse =
    isPaypalModal ||
    [
      ADDON,
      ADYEN,
      CYBERSOURCE,
      HOTELPAY,
      GETNET,
      MERCADOPAGO,
      MEWS,
      NUVEI,
      PAYBYRD,
      PAYCOMET,
      PAYMOB,
      PAYNOPAIN,
      REDUNIQ,
      SHIFT4,
    ].includes(provider) ||
    ([CECA, REDSYS, STRIPE, WORLDLINE].includes(provider) && methods[method]?.modal);
  const ownsModal = provider === SHIFT4 || isPaypalModal;
  const promiseData = { ...(gateway ? { gateway } : {}), ...(methodConfig ? { config: methodConfig } : {}) };

  return (
    <>
      <Button {...others} onPress={handlePress}>
        {children || translate(L10N.ACTION_BOOK_NOW)}
      </Button>

      {response?.type === TYPE.SUBMIT && <Form {...response} />}

      {React.createElement(
        ownsModal ? React.Fragment : Modal,
        {
          ...(!ownsModal
            ? {
                portal: isModalResponse,
                preventDefault: ![MERCADOPAGO, NUVEI].includes(provider),
                testId: others.testId && `${others.testId}-modal`,
                visible: modal,
                onClose: handleModalClose,
              }
            : undefined),
        },
        component &&
          React.createElement(component, {
            ...config,
            ...methodConfig,
            ...methods[method]?.prepayment,
            currencyDecimals,
            info,
            method,
            provider,
            publicKey,
            response,
            onClose: ownsModal ? handleModalClose : handleClose,
            onError,
            onSuccess: handleSuccess,
            className: style.modalContent,
          }),
      )}
    </>
  );
};

ButtonPayment.displayName = 'Mirai:Core:ButtonPayment';

ButtonPayment.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  config: PropTypes.shape({
    currencyDecimals: PropTypes.number,
    gateway: PropTypes.object,
    showCVV: PropTypes.bool,
  }),
  dataSource: PropTypes.shape({
    method: PropTypes.oneOf(Object.values(PAYMENT_METHOD)),
  }),
  hotel: PropTypes.shape({
    id: PropTypes.string,
    url: PropTypes.string,
  }),
  info: PropTypes.shape({
    amount: PropTypes.number,
    cardValidation: PropTypes.bool,
    date: PropTypes.instanceOf(Date),
  }),
  methods: PropTypes.objectOf(
    PropTypes.shape({
      component: PropTypes.number,
      modal: PropTypes.number,
      config: PropTypes.shape({
        id: PropTypes.oneOf(Object.values(PAYMENT_PROVIDER)),
        configurationId: PropTypes.number,
        publicKey: PropTypes.string,
      }),
      prepayment: PropTypes.shape({
        currency: PropTypes.string,
        value: PropTypes.number,
      }),
    }),
  ),
  promise: PropTypes.func,
  tracking: PropTypes.string,
  onError: PropTypes.func,
  onPress: PropTypes.func,
  onSuccess: PropTypes.func,
};

export { ButtonPayment };
